/* eslint-disable no-console */
import * as Sentry from '@sentry/react';

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LoggerOptions {
  level?: LogLevel;
  enabled?: boolean;
}

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

class ClientLogger {
  private level: LogLevel;
  private enabled: boolean;

  constructor(options: LoggerOptions = {}) {
    this.level = options.level ?? 'info';
    this.enabled = options.enabled ?? import.meta.env.DEV;
  }

  private shouldLog(level: LogLevel): boolean {
    return this.enabled && LOG_LEVELS[level] >= LOG_LEVELS[this.level];
  }

  private formatMessage(data: object | string, message?: string): string {
    if (typeof data === 'string') {
      return data;
    }
    const payload = JSON.stringify(data, null, 2);
    return message ? `${message}\n${payload}` : payload;
  }

  debug(data: object | string, message?: string): void {
    if (this.shouldLog('debug')) {
      console.debug(`[DEBUG] ${this.formatMessage(data, message)}`);
    }
  }

  info(data: object | string, message?: string): void {
    if (this.shouldLog('info')) {
      console.info(`[INFO] ${this.formatMessage(data, message)}`);
    }
  }

  warn(data: object | string, message?: string): void {
    if (this.shouldLog('warn')) {
      console.warn(`[WARN] ${this.formatMessage(data, message)}`);
      Sentry.addBreadcrumb({
        category: 'log',
        level: 'warning',
        message: typeof data === 'string' ? data : message,
        data: typeof data === 'object' ? data : undefined,
      });
    }
  }

  error(data: object | string, message?: string): void {
    if (this.shouldLog('error')) {
      console.error(`[ERROR] ${this.formatMessage(data, message)}`);
      Sentry.addBreadcrumb({
        category: 'log',
        level: 'error',
        message: typeof data === 'string' ? data : message,
        data: typeof data === 'object' ? data : undefined,
      });
    }
  }
}

export const logger = new ClientLogger({
  level: import.meta.env.DEV ? 'debug' : 'warn',
  enabled: true,
});
